/* LUXEMBOURG TCG — Card Shop UI. Uses Shopify search + Section Rendering API. */
(() => {
  'use strict';
  const walls = document.querySelectorAll('[data-ltcg-card-shop]');
  if (!walls.length) return;

  walls.forEach((wall) => {
    const searchUrl = wall.dataset.searchUrl || '/search';
    const resultSlot = wall.querySelector('[data-ltcg-results]');
    const searchInput = wall.querySelector('[data-ltcg-search-input]');
    const searchForm = wall.querySelector('[data-ltcg-search-form]');
    const loading = wall.querySelector('[data-ltcg-loading]');
    const clearSearch = wall.querySelector('[data-ltcg-clear-search]');
    const toast = wall.querySelector('[data-ltcg-toast]');
    const lightbox = wall.querySelector('[data-ltcg-lightbox]');
    // LTCG #6 (26 Oct 2026): the wall opens with "In stock only" switched on, so sold-out cards don't fill the first
    // pages. The switch stays visible and works as before; once a visitor turns it off (or clears all filters) it stays
    // off for tab changes and new searches. Set LTCG_DEFAULT_IN_STOCK to false to restore the old behaviour.
    const LTCG_DEFAULT_IN_STOCK = true;
    let stockOptOut = false;
    function baseFilters() {
      const f = new URLSearchParams();
      if (LTCG_DEFAULT_IN_STOCK && !stockOptOut) f.set('filter.v.availability', '1');
      return f;
    }
    const state = { query: '', category: 'all', sort: '', page: 1, filters: baseFilters() };
    let currentRequest = null;
    let debounce = null;
    let sequence = 0;

    // Browser uses actual Shopify collection URLs. Search remains storewide in this first stage.
    const collectionUrls = {
      all: wall.dataset.collectionAll,
      single: wall.dataset.collectionSingle,
      graded: wall.dataset.collectionGraded,
      promo: wall.dataset.collectionPromo
    };
    function isCollectionBrowse() { return state.query.length === 0; }
    function makeUrl(useSection = true) {
      const browsing = isCollectionBrowse();
      const url = new URL(browsing ? (collectionUrls[state.category] || collectionUrls.all) : searchUrl, window.location.origin);
      if (!browsing) {
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', state.query);
      }
      if (state.sort) url.searchParams.set('sort_by', state.sort);
      if (state.page > 1) url.searchParams.set('page', String(state.page));
      state.filters.forEach((value, key) => url.searchParams.append(key, value));
      // Pass theme preview parameter when editing an unpublished theme.
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      if (useSection) url.searchParams.set('section_id', 'ltcg-card-shop-results');
      else if (!browsing) url.searchParams.set('view', 'ltcg-card-shop');
      return url;
    }
    function showMessage(message, link) {
      toast.textContent = message;
      if (link) { const a = document.createElement('a'); a.href = link; a.textContent = wall.dataset.tViewCart || 'View cart'; toast.append(' ', a); }
      toast.hidden = false;
      window.clearTimeout(toast._ltcgTimeout);
      toast._ltcgTimeout = window.setTimeout(() => { toast.hidden = true; }, 4500);
    }

    // Add-to-cart via Horizon's own event contract, then make sure the cart drawer is open.
    // Horizon contract (product-form.js): POST /cart/add.js with the cart-items section ids in
    // `sections`, then dispatch @shopify/events CartLinesUpdateEvent (action 'add') whose promise
    // resolves to { cart, detail: { sections, itemCount, productId, source, didError } }.
    // cart-items-component morphs from detail.sections, cart-icon reads cart.totalQuantity, and
    // cart-drawer-component auto-opens when it has the auto-open attribute.
    function cartSectionIds() {
      return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))];
    }
    async function openCartDrawer(source, item, quantity, sections) {
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { credentials: 'same-origin' }).then((x) => x.json());
        const deferred = CartLinesUpdateEvent.createPromise();
        source.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: [{ merchandiseId: String(item.variant_id), quantity }],
          promise: deferred.promise
        }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'ltcg-card-shop', sourceId: 'ltcg-card-shop', itemCount: quantity, productId: String(item.product_id), sections, didError: false }
        });
        document.dispatchEvent(new CustomEvent('ltcg:cart-changed', { detail: { cart, productId: item?.product_id } }));
        // LTCG 26 Oct: the drawer stays closed after an add (only the header count updates and the
        // button flashes "✓ Added"); its contents are morphed from `sections` so it is current when opened.
        return true;
      } catch (e) { console.warn('[LTCG Card Shop] drawer refresh', e); }
      return false;
    }
    // LTCG-OVN perf 27 Oct (INP): give the browser a chance to paint / handle the next tap before the heavy
    // parse and before the grid swap (scheduler.yield where available, else a macrotask).
    const yieldToMain = () => (globalThis.scheduler && typeof scheduler.yield === 'function') ? scheduler.yield() : new Promise((r) => setTimeout(r, 0));
    async function requestMarkup(url, signal) {
      const response = await fetch(url, { signal, credentials: 'same-origin', headers: { 'Accept': 'text/html' } });
      if (!response.ok) throw new Error(`Shopify returned ${response.status}`);
      const html = await response.text();
      await yieldToMain();
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const found = doc.querySelector('[data-ltcg-response]');
      if (!found) throw new Error('Search result section was not returned');
      return found;
    }
    async function loadCards() {
      sequence += 1;
      const mySequence = sequence;
      if (currentRequest) currentRequest.abort();
      currentRequest = new AbortController();
      loading.hidden = false;
      resultSlot.setAttribute('aria-busy', 'true');
      try {
        let content;
        try {
          content = await requestMarkup(makeUrl(true), currentRequest.signal);
        } catch (error) {
          if (currentRequest.signal.aborted) throw error;
          // Fallback for stores that don't serve standalone section_id on /search.
          if (isCollectionBrowse()) throw error;
          content = await requestMarkup(makeUrl(false), currentRequest.signal);
        }
        if (sequence !== mySequence) return;
        if (state.query && !stockOptOut && state.filters.get('filter.v.availability') === '1' && !content.querySelector('.cs2-card')) {
          // LTCG #6: nothing in stock for this search, so show the sold-out matches instead of an empty wall.
          state.filters.delete('filter.v.availability');
          loadCards();
          return;
        }
        await yieldToMain();
        if (sequence !== mySequence) return;
        resultSlot.replaceChildren(content);
        if (sheetWasOpen) { setSheet(true); sheetWasOpen = false; } else { document.documentElement.classList.remove('cs2-lock'); }
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== mySequence) return;
        const fallback = makeUrl(false);
        fallback.searchParams.delete('view');
        resultSlot.innerHTML = '<div class="cs2__empty"><h3></h3><a data-ltcg-full-search href="#"></a></div>';
        resultSlot.querySelector('h3').textContent = wall.dataset.tError || 'Could not load the cards.';
        resultSlot.querySelector('a').textContent = wall.dataset.tOpen || 'Open the collection';
        const link = resultSlot.querySelector('[data-ltcg-full-search]');
        if (link) link.href = fallback.toString();
        console.error('[LTCG Card Shop]', error);
      } finally {
        if (sequence === mySequence) {
          loading.hidden = true;
          resultSlot.setAttribute('aria-busy', 'false');
        }
      }
    }
    function readFilters() {
      const filters = new URLSearchParams();
      const seen = new Set();
      resultSlot.querySelectorAll('[data-ltcg-filter]:checked').forEach((input) => {
        const key = input.name + '=' + input.value;
        if (!input.disabled && !seen.has(key)) { seen.add(key); filters.append(input.name, input.value); }
      });
      resultSlot.querySelectorAll('[data-ltcg-price]').forEach((input) => {
        if (seen.has(input.name)) return; seen.add(input.name);
        if (input.value !== '' && Number.isFinite(Number(input.value)) && Number(input.value) >= 0) {
          filters.append(input.name, input.value);
        }
      });
      const stockSwitch = resultSlot.querySelector('[data-ltcg-filter][name="filter.v.availability"][value="1"]');
      if (stockSwitch) stockOptOut = !filters.getAll('filter.v.availability').includes('1');
      return filters;
    }
    function updateSearch() {
      state.query = searchInput.value.trim();
      clearSearch.hidden = state.query.length === 0;
      state.page = 1;
      state.filters = baseFilters();
      loadCards();
    }
    searchInput.addEventListener('input', () => {
      clearSearch.hidden = !searchInput.value;
      window.clearTimeout(debounce);
      debounce = window.setTimeout(updateSearch, 450);
    });
    searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
      window.clearTimeout(debounce);
      updateSearch();
    });
    clearSearch.addEventListener('click', () => {
      searchInput.value = '';
      updateSearch();
      searchInput.focus();
    });
    function setSheet(open) {
      const sheet = resultSlot.querySelector('[data-ltcg-sheet]');
      const scrim = resultSlot.querySelector('.cs2__scrim');
      if (!sheet) return;
      sheet.classList.toggle('is-open', open);
      if (scrim) scrim.hidden = !open;
      document.documentElement.classList.toggle('cs2-lock', open);
      resultSlot.querySelectorAll('[data-ltcg-open-sheet]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
      if (open) sheet.querySelector('button, select, input')?.focus();
    }
    let sheetWasOpen = false;
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setSheet(false); });
    wall.addEventListener('click', (event) => {
      if (event.target.closest('[data-ltcg-open-sheet]')) { setSheet(true); return; }
      if (event.target.closest('[data-ltcg-close-sheet]')) { setSheet(false); return; }
      const qtyBtn = event.target.closest('[data-ltcg-qty]');
      if (qtyBtn) {
        const input = qtyBtn.parentElement.querySelector('[data-ltcg-qty-input]');
        const max = Number(input.max) || 99;
        input.value = String(Math.min(max, Math.max(1, (Number(input.value) || 1) + Number(qtyBtn.dataset.ltcgQty))));
        return;
      }
      const chip = event.target.closest('[data-ltcg-remove-name]');
      if (chip) {
        resultSlot.querySelectorAll('[data-ltcg-filter]').forEach((input) => {
          if (input.name === chip.dataset.ltcgRemoveName && input.value === chip.dataset.ltcgRemoveValue) input.checked = false;
        });
        state.filters = readFilters(); state.page = 1; loadCards(); return;
      }
      const priceChip = event.target.closest('[data-ltcg-remove-price]');
      if (priceChip) {
        resultSlot.querySelectorAll('[data-ltcg-price]').forEach((input) => { if (input.name === priceChip.dataset.ltcgRemovePrice) input.value = ''; });
        state.filters = readFilters(); state.page = 1; loadCards(); return;
      }
      const tab = event.target.closest('[data-ltcg-category]');
      if (tab) {
        state.category = tab.dataset.ltcgCategory;
        state.sort = '';
        state.query = '';
        searchInput.value = '';
        clearSearch.hidden = true;
        state.page = 1;
        state.filters = baseFilters();
        wall.querySelectorAll('[data-ltcg-category]').forEach((button) => {
          const active = button === tab;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        loadCards();
        return;
      }
      const page = event.target.closest('[data-ltcg-page]');
      if (page && !page.disabled) {
        state.page = Math.max(1, Number(page.dataset.ltcgPage) || 1);
        loadCards();
        wall.querySelector('.ltcg-wall__section-title--browse')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (event.target.closest('[data-ltcg-clear-filters]')) {
        stockOptOut = true;
        state.filters = new URLSearchParams();
        state.page = 1;
        loadCards();
        return;
      }
      if (event.target.closest('[data-ltcg-reset]')) {
        state.query = '';
        state.category = 'all';
        state.sort = '';
        state.filters = baseFilters();
        state.page = 1;
        searchInput.value = '';
        clearSearch.hidden = true;
        wall.querySelectorAll('[data-ltcg-category]').forEach((button) => {
          const active = button.dataset.ltcgCategory === 'all';
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        loadCards();
        return;
      }
      if (event.target.closest('[data-ltcg-apply-price]')) {
        state.filters = readFilters();
        state.page = 1;
        loadCards();
        return;
      }
      const zoom = event.target.closest('[data-ltcg-zoom]');
      if (zoom && lightbox && typeof lightbox.showModal === 'function') {
        // LTCG-FIXES r1: create the lightbox image on first open (no empty <img> in the HTML)
        let lbImg = lightbox.querySelector('[data-ltcg-lightbox-image]');
        if (!lbImg) {
          lbImg = document.createElement('img');
          lbImg.setAttribute('data-ltcg-lightbox-image', '');
          lbImg.width = 700;
          lbImg.height = 980;
          lightbox.querySelector('[data-ltcg-lightbox-label]').before(lbImg);
        }
        lbImg.alt = zoom.dataset.label || '';
        lbImg.src = zoom.dataset.src;
        lightbox.querySelector('[data-ltcg-lightbox-label]').textContent = zoom.dataset.label;
        lightbox.showModal();
        return;
      }
      if (event.target.closest('[data-ltcg-close-lightbox]')) {
        lightbox?.close();
        return;
      }
      const add = event.target.closest('[data-ltcg-add]');
      if (add && !add.disabled) {
        add.disabled = true;
        const label = add.querySelector('span') || add;
        label.textContent = wall.dataset.tAdding || 'Adding…';
        const qtyInput = add.closest('.cs2-card__buy')?.querySelector('[data-ltcg-qty-input]');
        const quantity = Math.max(1, Number(qtyInput?.value) || 1);
        fetch(wall.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(add.dataset.variantId), quantity, sections: cartSectionIds().join(','), sections_url: window.location.pathname })
        }).then(async (response) => {
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            // LTCG R2 29 Oct (L6): stock-limit errors (422) in the shop language. Only claim "we added the maximum"
            // when the cart really holds this variant; otherwise fall back to Shopify's own message.
            if (response.status === 422 && wall.dataset.tStockMax) {
              const c = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { credentials: 'same-origin' }).then((x) => x.json()).catch(() => null);
              const line = c && (c.items || []).find((i) => String(i.variant_id) === String(add.dataset.variantId));
              if (line) {
                document.dispatchEvent(new CustomEvent('ltcg:cart-changed', { detail: { cart: c, productId: line.product_id } }));
                throw new Error(wall.dataset.tStockMax.replace('{n}', line.quantity));
              }
            }
            throw new Error(error.description || 'Could not add card to cart');
          }
          const item = await response.json().catch(() => ({}));
          label.textContent = wall.dataset.tAdd || 'Add';
          add.disabled = false;
          add.classList.add('is-added');
          setTimeout(() => add.classList.remove('is-added'), 1500);
          const opened = await openCartDrawer(add, item, quantity, item.sections);
          if (opened && window.ltcgFlashAdded) window.ltcgFlashAdded(add, null, true);
          if (!opened) showMessage(wall.dataset.tAdded || 'Added to cart', wall.dataset.cartUrl || '/cart');
        }).catch((error) => {
          showMessage(error.message);
          add.disabled = false;
          label.textContent = wall.dataset.tAdd || 'Add';
        });
      }
    });
    wall.addEventListener('change', (event) => {
      if (event.target.matches('[data-ltcg-sort]')) {
        sheetWasOpen = !!event.target.closest('[data-ltcg-sheet].is-open');
        state.sort = event.target.value;
        state.page = 1;
        loadCards();
      } else if (event.target.matches('[data-ltcg-filter]')) {
        resultSlot.querySelectorAll('[data-ltcg-filter]').forEach((input) => {
          if (input !== event.target && input.name === event.target.name && input.value === event.target.value) input.checked = event.target.checked;
        });
        sheetWasOpen = !!resultSlot.querySelector('[data-ltcg-sheet].is-open');
        // An 'entire era' selection must not conflict with specific sets.
        // Individual set selections can span several eras (native OR within a filter).
        if (event.target.checked && event.target.matches('[data-ltcg-era-all]')) {
          resultSlot.querySelectorAll('[data-ltcg-set-filter], [data-ltcg-era-all]').forEach((input) => {
            if (input !== event.target) input.checked = false;
          });
        } else if (event.target.checked && event.target.matches('[data-ltcg-set-filter]')) {
          resultSlot.querySelectorAll('[data-ltcg-era-all]').forEach((input) => {
            input.checked = false;
          });
        }
        state.filters = readFilters();
        state.page = 1;
        loadCards();
      }
    });
    if (lightbox) lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) lightbox.close();
    });
    loading.hidden = false;
    loadCards();
  });
})();
